import { environment } from "src/environments/environment";
import { Capabilities } from "../shared/stores/current-user.store";

export interface NavItem {
  labelKey: string;
  route?: string;
  routeLocalized?: { key: string; params?: string[] };
  externalUrl?: string;
  queryParams?: Record<string, string>;
  children?: NavItem[];
  standalone?: boolean;
  /**
   * Optional capability gate. When set, the nav templates only render this
   * item if the current user has the capability — see HasCapabilityDirective.
   */
  capability?: keyof Capabilities;
}

export type NavProfile = "guest" | "student" | "teacher" | "company" | "admin";

// Shared dropdown items reused across profiles
const overOns: NavItem[] = [
  {
    labelKey: "Our Story",
    routeLocalized: { key: "pages", params: ["over-ons"] },
  },
  {
    labelKey: "Meet the Team",
    routeLocalized: { key: "pages", params: ["over-ons"] },
  },
  { labelKey: "Meet CHILL", route: "/meet-chill" },
];

const dienstenFull: NavItem[] = [
  { labelKey: "Platform", route: "/explanation-platform" },
  { labelKey: "AWVL", externalUrl: environment.awvlUrl },
  {
    labelKey: "Education system",
    routeLocalized: { key: "pages", params: ["onderwijs"] },
  },
  {
    labelKey: "Serious Gaming",
    routeLocalized: { key: "pages", params: ["serious-gaming"] },
  },
];

const dienstenStudent: NavItem[] = [
  { labelKey: "Platform", route: "/explanation-platform" },
  { labelKey: "AWVL", externalUrl: environment.awvlUrl },
];

// Follow-up days is gated by `canViewFollowUpDays` (Student && !Professional)
// so it only renders for non-Professional students. Listed in the shared
// "Opportunities" config but the directive does the actual filtering.
const followUpDaysItem: NavItem = {
  labelKey: "Follow-up days",
  route: "/experiencedays",
  capability: "canViewFollowUpDays",
};

const kansen: NavItem[] = [
  { labelKey: "Events", routeLocalized: { key: "events" } },
  { labelKey: "Our network", routeLocalized: { key: "our-network" } },
  { labelKey: "Vacancies", routeLocalized: { key: "vacancies" } },
  followUpDaysItem,
  { labelKey: "Announcements", route: "/call-for-opportunities" },
];

const ontdek: NavItem[] = [
  { labelKey: "Projects", routeLocalized: { key: "projects" } },
  { labelKey: "News", route: "/blog" },
  {
    labelKey: "Knowledge base",
    routeLocalized: { key: "pages", params: ["kennisdatabank"] },
  },
];

export function getNavItems(profile: NavProfile): NavItem[] {
  const dropdowns: NavItem[] = [
    { labelKey: "About us", children: overOns },
    {
      labelKey: "Services",
      children: profile === "student" ? dienstenStudent : dienstenFull,
    },
    { labelKey: "Opportunities", children: kansen },
    { labelKey: "Discover", children: ontdek },
  ];

  const standalone: NavItem[] = [];

  switch (profile) {
    case "student":
      standalone.push(
        {
          labelKey: "Companies",
          route: "/student/companies",
          standalone: true,
        },
        { labelKey: "Vacancies", route: "/student/jobs", standalone: true },
        {
          labelKey: "Announcements",
          route: "/call-for-opportunities",
          standalone: true,
        }
      );
      break;
    case "teacher":
      standalone.push({
        labelKey: "My classes",
        route: "/profile",
        queryParams: { tab: "students" },
        standalone: true,
      });
      break;
    case "admin":
      break;
    case "company":
      standalone.push(
        { labelKey: "My company", route: "/company/profile", standalone: true },
        {
          labelKey: "Candidates",
          route: "/company/students",
          standalone: true,
        },
        {
          labelKey: "Announcements",
          route: "/company/opportunities",
          standalone: true,
        }
      );
      break;
  }

  return [...dropdowns, ...standalone];
}

export const LANDING_NAV_ITEMS: NavItem[] = [
  { labelKey: "Pupil", route: "/pupil" },
  { labelKey: "Student", route: "/student" },
  { labelKey: "Young Professional", route: "/young-professional" },
  { labelKey: "Professional", route: "/professional" },
  { labelKey: "International", route: "/international" },
  { labelKey: "Teacher", route: "/teacher" },
  { labelKey: "Company", route: "/company" },
];
